import { DeleteIcon, Group } from '@/components/_common';
import StatusTag from '@/components/_common/StatusTag';
import {
  WorkOrderImportGeneralFormValues,
  WorkOrderImportFormItem,
} from '@/core/domain/workorder/WorkOrdersImports/WorkOrderImports.types';
import {
  WorkOrderItemType,
  workorderTypeColor,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useDisclosure } from '@/hooks';
import { quantityFormatter } from '@/utils/number';
import { COLUMNS_WIDTH } from '@/utils/table';
import {
  Button,
  Card,
  Checkbox,
  Divider,
  Dropdown,
  Form,
  FormInstance,
  Popover,
  Space,
  Table,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useEffect, useState } from 'react';
import { FaExchangeAlt, FaQuestionCircle } from 'react-icons/fa';
import { useIntl } from 'react-intl';
import ImportChangeItemsModal from './ImportChangeItemsModal';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';
import formatterPrice from '@/utils/number/formatterPrice';
import ActionIcon from '@/components/_common/ActionIcon';
import { RefreshCw, Trash } from 'react-feather';

type Props = {
  form: FormInstance<WorkOrderImportGeneralFormValues>;
};

const WorkorderImportItems: React.FC<Props> = ({ form }) => {
  const intl = useIntl();
  const [selectAll, setSelectAll] = useState(false);
  const items = Form.useWatch('items', form);
  const isAllSelected = items?.every((item) => item.isInternal) || false;
  const [itemSelected, setItemSelected] = useState<
    WorkOrderImportFormItem & { index?: number }
  >();
  const [modalOpened, modalAction] = useDisclosure(false, {
    onClose: () => setItemSelected(undefined),
  });

  const [keysSelectedToRemove, setKeysSelectedToRemove] = useState<string[]>(
    [],
  );
  const [keysSelectedToRestore, setKeysSelectedToRestore] = useState<string[]>(
    [],
  );
  const [itemsRemoved, setItemsRemoved] = useState<WorkOrderImportFormItem[]>(
    [],
  );

  useEffect(() => {
    if (form) {
      form.setFieldValue(
        'items',
        items?.map((item) => ({
          ...item,
          isInternal: selectAll,
        })) || [],
      );
    }
  }, [selectAll, form]);

  function getItemLabel(item: WorkOrderImportFormItem): string {
    if (item.type === WorkOrderItemType.Part) {
      if (item.isProvided) return 'Peça: Fornecida';
      if (item.isTypedPart) return 'Peça: Digitada';
      return 'Peça: Cadastrada';
    } else {
      return item.isProvided ? 'Serviço: Terceiros' : 'Serviço: Digitado';
    }
  }

  const columns = (
    removedColumns = false,
  ): ColumnsType<WorkOrderImportFormItem> => {
    const dataColumns: ColumnsType<WorkOrderImportFormItem> = [
      {
        key: 'type',
        dataIndex: 'type',
        title: intl.formatMessage({
          id: 'workOrders.domain.items.type',
        }),
        width: COLUMNS_WIDTH.action1,
        ellipsis: true,
        render: (_, item, index) => {
          return (
            <>
              <Form.Item name={[index, 'subGroupId']} hidden />
              <Form.Item name={[index, 'manufacturerId']} hidden />
              <Form.Item name={[index, 'subAreaId']} hidden />
              <Form.Item name={[index, 'isProvided']} hidden />
              <Form.Item name={[index, 'isTypedPart']} hidden />

              {item.type === WorkOrderItemType.Part ||
              item.type === WorkOrderItemType.Service ? (
                <StatusTag
                  label={getItemLabel(item)}
                  color={workorderTypeColor[item.type]}
                  value={String(item.isTypedPart || 'false')}
                  disabled={removedColumns}
                  disabledOption={
                    item.type === WorkOrderItemType.Service || item.isProvided
                  }
                  onStatusChange={(value: string) =>
                    form.setFieldValue(
                      ['items', index, 'isTypedPart'],
                      value === 'true',
                    )
                  }
                  options={[
                    { label: 'Peça: Cadastrada', value: 'false' },
                    { label: 'Peça: Digitada', value: 'true' },
                  ]}
                />
              ) : (
                '-'
              )}
            </>
          );
        },
      },
      {
        dataIndex: 'code',
        title: 'Código',
        width: COLUMNS_WIDTH.default,
        ellipsis: true,
        render: (value: string) => value || '-',
      },
      {
        dataIndex: 'description',
        title: intl.formatMessage({
          id: 'workOrders.domain.items.description',
        }),
        width: 300,
        ellipsis: true,
        render: (value: string) => (
          <EllipsisHoverToggle>{value || '-'}</EllipsisHoverToggle>
        ),
      },
      {
        dataIndex: 'quantity',
        width: COLUMNS_WIDTH.extraSmall,
        ellipsis: true,
        title: intl.formatMessage({
          id: 'workOrders.domain.items.quantityTime',
        }),
        align: 'right',
        render: (value: number) =>
          typeof value === 'number' ? quantityFormatter(value) : '-',
      },
      {
        dataIndex: 'unitPrice',
        title: intl.formatMessage({
          id: 'workOrders.domain.items.unitPrice',
        }),
        width: COLUMNS_WIDTH.price,
        ellipsis: true,
        align: 'right',
        render: (value: number) => formatterPrice(value),
      },
      {
        dataIndex: 'discount',
        title: intl.formatMessage({
          id: 'workOrders.domain.items.discount',
        }),
        width: COLUMNS_WIDTH.price,
        ellipsis: true,
        align: 'right',
        render: (value: number) => formatterPrice(value * 100),
      },
      {
        title: intl.formatMessage({
          id: 'workOrders.domain.items.discountValue',
        }),
        align: 'right',
        width: COLUMNS_WIDTH.price,
        ellipsis: true,
        render: (_, row) =>
          formatterPrice(row.quantity * row.unitPrice * row.discount),
      },
      {
        title: intl.formatMessage({
          id: 'workOrders.domain.items.totalValue',
        }),
        align: 'right',
        width: COLUMNS_WIDTH.price,
        ellipsis: true,
        render: (_, row) =>
          formatterPrice(
            row.quantity * row.unitPrice -
              row.quantity * row.unitPrice * row.discount,
          ),
      },
      {
        key: 'changePart',
        width: COLUMNS_WIDTH.small,
        align: 'right',
        title: (
          <Group position="right">
            <Space split={<Divider type="vertical" />}>
              <Checkbox
                checked={selectAll}
                onChange={({ target }) =>
                  setSelectAll(!isAllSelected && target.checked)
                }
              />

              <Dropdown
                placement="bottomRight"
                overlay={
                  <Card
                    bodyStyle={{ padding: 12 }}
                    style={{ maxWidth: 200, borderRadius: 5 }}
                  >
                    Registrar como uma nova peça
                    <Divider style={{ marginTop: 5, marginBottom: 5 }} />
                    Quando uma peça importada não é encontrada em sua base de
                    dados, a mesma é marcada para ser registrada como uma nova
                    peça em sua base.
                  </Card>
                }
              >
                <Button
                  type="text"
                  size="small"
                  icon={<FaQuestionCircle size={16} />}
                  style={{
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                />
              </Dropdown>
            </Space>
          </Group>
        ),
        render: (_, item, index) => (
          <Space split={<Divider type="vertical" />}>
            {item.type === WorkOrderItemType.Part && (
              <Form.Item
                noStyle
                name={[index, 'isInternal']}
                valuePropName="checked"
              >
                <Checkbox disabled={item.isInternalDisabled} />
              </Form.Item>
            )}
            <Button
              type="text"
              size="small"
              icon={<FaExchangeAlt size={16} />}
              style={{
                display: 'inline-flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              onClick={() => {
                setItemSelected({ ...item, index });
                modalAction.open();
              }}
            />
          </Space>
        ),
      },
    ];

    if (removedColumns) {
      return dataColumns.filter(
        (column) => !['changePart'].includes(column.key as string),
      );
    }

    return dataColumns;
  };

  const removeItemsSelection = () => {
    const formItems: WorkOrderImportFormItem[] =
      form.getFieldValue('items') || [];

    const itemsRemoved = formItems.filter((item) =>
      keysSelectedToRemove.includes(item.id),
    );

    setItemsRemoved((prev) => [...prev, ...itemsRemoved]);
    form.setFieldValue(
      'items',
      formItems.filter(
        (formItem) => !keysSelectedToRemove.includes(formItem.id),
      ),
    );
    setKeysSelectedToRemove([]);
  };

  const restoreItemsSelection = () => {
    const formItems = form.getFieldValue('items') as WorkOrderImportFormItem[];

    form.setFieldValue('items', [
      ...formItems,
      ...itemsRemoved.filter((formItem) =>
        keysSelectedToRestore.includes(formItem.id),
      ),
    ]);

    setItemsRemoved((prev) =>
      prev.filter((prevItem) => !keysSelectedToRestore.includes(prevItem.id)),
    );

    setKeysSelectedToRestore([]);
  };

  return (
    <>
      <Form form={form}>
        <Typography.Title style={{ marginTop: 0 }} level={3}>
          Itens para importação
        </Typography.Title>

        <Form.Item shouldUpdate={(prev, cur) => prev.items !== cur.items}>
          {({ getFieldValue }) => {
            const formItems: WorkOrderImportFormItem[] =
              getFieldValue('items') || [];

            return (
              <>
                <Table<WorkOrderImportFormItem>
                  style={{ marginBottom: 24 }}
                  columns={[
                    ...columns(),
                    {
                      key: 'action',
                      title: 'Ação',
                      width: 60,
                      fixed: 'right',
                      align: 'center',
                      render: (_, item) => (
                        <Group position="center">
                          <DeleteIcon
                            onClick={() => {
                              setItemsRemoved((prev) => [...prev, item]);
                              form.setFieldValue(
                                'items',
                                formItems.filter(
                                  (formItem) => formItem.id !== item.id,
                                ),
                              );
                            }}
                          />
                        </Group>
                      ),
                    },
                  ]}
                  dataSource={[
                    ...(formItems?.filter(
                      (item) => item.type === WorkOrderItemType.Part,
                    ) || []),
                    ...(formItems?.filter(
                      (item) => item.type !== WorkOrderItemType.Part,
                    ) || []),
                  ]}
                  rowKey={(item) => item.id}
                  scroll={{ x: true }}
                  pagination={false}
                  loading={!items}
                  rowSelection={{
                    selectedRowKeys: keysSelectedToRemove,
                    onChange: (keys) => {
                      setKeysSelectedToRemove(keys as string[]);
                    },
                  }}
                />

                <Button
                  type="primary"
                  danger
                  icon={<Trash size={16} />}
                  disabled={!keysSelectedToRemove.length}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    lineHeight: 1,
                  }}
                  onClick={removeItemsSelection}
                >
                  Remover itens
                </Button>
              </>
            );
          }}
        </Form.Item>
      </Form>

      {!!itemsRemoved.length && (
        <>
          <Typography.Title style={{ marginTop: 0 }} level={3}>
            Itens removidos
          </Typography.Title>

          <Table
            style={{ marginBottom: 24 }}
            columns={[
              ...columns(true),
              {
                key: 'action',
                title: 'Ação',
                width: 60,
                fixed: 'right',
                align: 'center',
                render: (_, item) => (
                  <Group position="center">
                    <Popover content="Restaurar item">
                      <ActionIcon
                        onClick={() => {
                          setItemsRemoved((prev) =>
                            prev.filter((prevItem) => prevItem.id !== item.id),
                          );

                          const formItems: WorkOrderImportFormItem[] =
                            form.getFieldValue('items') || [];

                          form.setFieldValue('items', [...formItems, item]);
                        }}
                      >
                        <RefreshCw size={20} />
                      </ActionIcon>
                    </Popover>
                  </Group>
                ),
              },
            ]}
            dataSource={itemsRemoved}
            rowKey={(item) => item.id}
            scroll={{ x: true }}
            pagination={false}
            rowSelection={{
              selectedRowKeys: keysSelectedToRestore,
              onChange: (keys) => {
                setKeysSelectedToRestore(keys as string[]);
              },
            }}
          />

          <Button
            type="primary"
            icon={<RefreshCw size={16} />}
            disabled={!keysSelectedToRestore.length}
            style={{
              display: 'flex',
              alignItems: 'center',
              lineHeight: 1,
            }}
            onClick={restoreItemsSelection}
          >
            Restaurar itens
          </Button>
        </>
      )}

      <ImportChangeItemsModal
        onConfirm={(data) => {
          form.setFieldValue(['items', itemSelected?.index as number], {
            ...itemSelected,
            description: data.name,
            code: data.manufacturingCode,
            itemId: data.id,
            register: true,
            isInternal: false,
          });
          modalAction.close();
        }}
        item={itemSelected}
        onCancel={modalAction.close}
        open={modalOpened}
      />
    </>
  );
};

export default WorkorderImportItems;
